// pages/SAPProcessMapFindings.jsx — "Findings & notes" tab of the Process Map.
// Shows the review recap per landscape and, for admins, a notes editor on top of
// table procmap_note (edit / hide / mark reviewed / add), the snapshot status and a
// manual refresh. Notes flagged "needs review" come from the monthly refresh when a
// figure they quote moved more than 20 %.
import { useState } from 'react';
import { api } from '../context/AuthContext';
import { loadLive, GROUP_ORDER, GROUP_LABEL } from './sapProcessMapLive';
import { TOPICS, OPS, TL_KIND, tlKind } from './sapProcessMapOps';

const KIND_LABEL = {
  finding: { en: 'Findings', id: 'Temuan' },
  timeline: { en: 'Timeline', id: 'Linimasa' },
  fact: { en: 'Landscape facts (About tab)', id: 'Fakta landscape (tab Tentang)' },
  step: { en: 'Step notes & custom points', id: 'Catatan langkah & custom' },
  std_step: { en: 'Standard comparison — steps', id: 'Perbandingan standar — langkah' },
  std_diff: { en: 'Standard comparison — differences', id: 'Perbandingan standar — perbedaan' },
  org: { en: 'Organisation notes', id: 'Catatan organisasi' },
};

const T = {
  en: { data: 'Figures', snap: 'snapshot', static: 'static snapshot (no database snapshot yet)', refresh: 'Refresh now', running: 'Refresh running…',
    started: 'Refresh started. It reads P01 for a few minutes; reload the page afterwards.', review: 'needs review', reviewed: 'Mark reviewed', edit: 'Edit', hide: 'Hide',
    save: 'Save', cancel: 'Cancel', add: 'Add finding', editor: 'Notes editor', showEditor: 'Edit all notes of this landscape', hideEditor: 'Close editor',
    onlyReview: 'Only notes that need review', toReview: n => `${n} note(s) need review after the last refresh`, group: 'Group', when: 'When', none: 'Nothing here.',
    hidden: 'hidden', show: 'Show', lastRuns: 'Recent refreshes' },
  id: { data: 'Angka', snap: 'snapshot', static: 'snapshot statis (belum ada snapshot di database)', refresh: 'Refresh sekarang', running: 'Refresh sedang berjalan…',
    started: 'Refresh dimulai. Membaca P01 beberapa menit; muat ulang halaman setelahnya.', review: 'perlu ditinjau', reviewed: 'Tandai sudah ditinjau', edit: 'Ubah', hide: 'Sembunyikan',
    save: 'Simpan', cancel: 'Batal', add: 'Tambah temuan', editor: 'Editor catatan', showEditor: 'Ubah semua catatan landscape ini', hideEditor: 'Tutup editor',
    onlyReview: 'Hanya catatan yang perlu ditinjau', toReview: n => `${n} catatan perlu ditinjau setelah refresh terakhir`, group: 'Grup', when: 'Waktu', none: 'Tidak ada.',
    hidden: 'disembunyikan', show: 'Tampilkan', lastRuns: 'Refresh terakhir' },
};

const btn = 'rounded-md border px-2 py-0.5 text-[11.5px] font-semibold';

const yearOf = w => { const m = String(w).match(/(19|20)\d\d/); return m ? m[0] : String(w); };

function NoteEditor({ n, lang, onDone }) {
  const [en, setEn] = useState(n ? n.text_en : '');
  const [id, setId] = useState(n ? n.text_id : '');
  const [busy, setBusy] = useState(false);
  const t = T[lang];
  const save = async () => {
    setBusy(true);
    try { await onDone({ text_en: en.trim(), text_id: id.trim() || en.trim() }); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-1.5 w-full">
      <textarea value={en} onChange={e => setEn(e.target.value)} rows={2} placeholder="English"
        className="w-full rounded-md border border-slate-300 px-2 py-1 text-[12.5px]" />
      <textarea value={id} onChange={e => setId(e.target.value)} rows={2} placeholder="Bahasa Indonesia"
        className="w-full rounded-md border border-slate-300 px-2 py-1 text-[12.5px]" />
      <div className="flex gap-1.5">
        <button type="button" disabled={busy || !en.trim()} onClick={save} className={`${btn} border-brand-600 bg-brand-600 text-white disabled:opacity-50`}>{t.save}</button>
        <button type="button" onClick={() => onDone(null)} className={`${btn} border-slate-200 text-slate-600`}>{t.cancel}</button>
      </div>
    </div>
  );
}

function NoteLine({ n, text, lang, admin, onChanged }) {
  const [editing, setEditing] = useState(false);
  const t = T[lang];
  const put = async body => { await api.put(`/api/sap-procmap/notes/${n.id}`, body); await onChanged(); };
  return (
    <li className={`group ${n && n.active === 0 ? 'opacity-50' : ''}`}>
      {editing ? (
        <NoteEditor n={n} lang={lang} onDone={async v => { if (v) await put(v); setEditing(false); }} />
      ) : (
        <span>
          {text}
          {n && n.needs_review === 1 && (
            <span title={n.review_reason || ''} className="ml-1.5 rounded border border-amber-300 bg-amber-50 px-1 text-[10.5px] font-semibold text-amber-700">{t.review}</span>
          )}
          {n && n.active === 0 && <span className="ml-1.5 text-[10.5px] text-slate-400">({t.hidden})</span>}
          {admin && n && (
            <span className={`ml-2 inline-flex gap-1 align-middle transition-opacity ${n.needs_review === 1 || n.active === 0 ? '' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'}`}>
              {n.needs_review === 1 && (
                <button type="button" title={t.reviewed} onClick={async () => { await api.post(`/api/sap-procmap/notes/${n.id}/reviewed`); await onChanged(); }}
                  className="rounded border border-emerald-300 px-1 text-[10.5px] text-emerald-700 hover:bg-emerald-50">✓</button>
              )}
              <button type="button" title={t.edit} onClick={() => setEditing(true)} className="rounded border border-slate-200 px-1 text-[10.5px] text-slate-500 hover:bg-slate-50">✎</button>
              <button type="button" title={n.active === 0 ? t.show : t.hide} onClick={() => put({ active: n.active === 0 ? 1 : 0 })}
                className="rounded border border-slate-200 px-1 text-[10.5px] text-slate-500 hover:bg-slate-50">{n.active === 0 ? '↺' : '✕'}</button>
            </span>
          )}
        </span>
      )}
    </li>
  );
}

export default function SAPProcessMapFindings({ landKey, lang, find, snap, live }) {
  const t = T[lang];
  const admin = !!(live && live.admin);
  const [msg, setMsg] = useState('');
  const [editor, setEditor] = useState(false);
  const [onlyReview, setOnlyReview] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addGrp, setAddGrp] = useState('summary');
  const refresh = () => loadLive(true);
  const [q, setQ] = useState('');
  const [topic, setTopic] = useState(null);
  const [openYears, setOpenYears] = useState({});
  const hit = x => {
    const s = `${x.en || ''} ${x.id || ''}`;
    if (topic && !TOPICS.find(t2 => t2.k === topic).re.test(s)) return false;
    if (q.trim()) return q.trim().toLowerCase().split(/\s+/).every(w => s.toLowerCase().includes(w));
    return true;
  };
  const filtering = !!(topic || q.trim());
  const groups = find ? find.groups.map(g => ({ ...g, items: g.items.filter(hit) })).filter(g => g.items.length) : [];
  const tl = find && find.timeline ? find.timeline.filter(([, x]) => hit(x)) : [];
  const years = [];
  tl.forEach(([w, x]) => { const y = yearOf(w); let e = years.find(v => v.y === y); if (!e) { e = { y, rows: [] }; years.push(e); } e.rows.push([w, x]); });
  const ops = OPS[landKey] || [];

  const allNotes = ((live && live.notes) || []).filter(n => n.land === landKey);
  const toReview = allNotes.filter(n => n.active && n.needs_review === 1);
  const running = live && live.running;

  const startRefresh = async () => {
    try { await api.post('/api/sap-procmap/refresh', {}); setMsg(t.started); refresh(); }
    catch (e) { setMsg(e.response?.data?.error || e.message); }
  };
  const addFinding = async v => {
    if (v) { await api.post('/api/sap-procmap/notes', { land: landKey, kind: 'finding', grp: addGrp, ...v }); await refresh(); }
    setAdding(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-col gap-4">
      {/* data status */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-slate-500 border-b border-slate-100 pb-2">
        <span>
          {t.data}: {snap && snap.live
            ? <><b className="text-slate-700">{lang === 'id' ? (snap.periodId || snap.period) : snap.period}</b> · {t.snap} #{snap.id} · {snap.takenOn}</>
            : <>{snap?.period} · {t.static}</>}
        </span>
        {toReview.length > 0 && (
          <button type="button" onClick={() => { setEditor(true); setOnlyReview(true); }}
            className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11.5px] font-semibold text-amber-700">⚠ {t.toReview(toReview.length)}</button>
        )}
        {admin && (
          <span className="ml-auto flex gap-1.5">
            <button type="button" disabled={!!running} onClick={startRefresh}
              className={`${btn} border-brand-300 text-brand-700 hover:bg-brand-50 disabled:opacity-50`}>{running ? t.running : '⟳ ' + t.refresh}</button>
            <button type="button" onClick={() => setEditor(e => !e)} className={`${btn} border-slate-200 text-slate-600 hover:bg-slate-50`}>{editor ? t.hideEditor : '✎ ' + t.showEditor}</button>
          </span>
        )}
        {msg && <span className="w-full text-[11.5px] text-brand-700">{msg}</span>}
      </div>

      {/* operational calendar: recurring activities that explain many user complaints */}
      {ops.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{lang === 'id' ? 'Kalender operasional — cek ini dulu saat ada keluhan user' : 'Operational calendar — check this first when a user reports an issue'}</div>
          <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            {ops.map(o => (
              <div key={o.when.en} className="rounded-lg border border-slate-200 px-3 py-2 flex flex-col gap-1" style={{ borderTop: `3px solid ${o.color}` }}>
                <div className="text-[12.5px] font-bold" style={{ color: o.color }}>{o.when[lang]}</div>
                <div className="text-[12.5px] text-slate-600">{o.what[lang]}</div>
                <div className="text-[12px] text-slate-700 bg-slate-50 rounded px-2 py-1 mt-auto">💡 {o.symptom[lang]}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* search + topic chips */}
      {find && (
        <div className="flex flex-wrap items-center gap-1.5">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={lang === 'id' ? 'Cari temuan & linimasa… (mis. lock, ZBS05, kurs)' : 'Search findings & timeline… (e.g. lock, ZBS05, kurs)'}
            className="w-full sm:w-80 rounded-md border border-slate-300 px-2.5 py-1 text-[12.5px] mr-1" />
          {TOPICS.map(t2 => (
            <button key={t2.k} type="button" onClick={() => setTopic(v => v === t2.k ? null : t2.k)}
              className={`rounded-full px-2.5 py-0.5 text-[12px] border font-semibold ${topic === t2.k ? 'bg-brand-600 border-brand-600 text-white' : 'border-slate-200 text-slate-600 hover:border-brand-400'}`}>{t2[lang]}</button>
          ))}
          {filtering && <button type="button" onClick={() => { setQ(''); setTopic(null); }} className={`${btn} border-slate-200 text-slate-500`}>✕ {lang === 'id' ? 'Reset' : 'Clear'}</button>}
        </div>
      )}
      {filtering && !groups.length && !tl.length && <div className="text-[12.5px] text-slate-500">{t.none}</div>}

      {/* findings */}
      {find && (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          {groups.map(g => (
            <div key={g.key} className={`flex flex-col gap-1.5 min-w-0 ${g.key === 'risks' ? 'rounded-lg bg-rose-50 border border-rose-100 px-3 py-2' : ''}`}>
              <div className={`text-[11px] font-bold uppercase tracking-wider ${g.key === 'risks' ? 'text-rose-700' : 'text-slate-400'}`}>{(GROUP_LABEL[g.key] || { en: g.key, id: g.key })[lang]}</div>
              <ul className="list-disc pl-4 text-[12.5px] text-slate-600 space-y-1">
                {g.items.map(x => <NoteLine key={x._n ? x._n.id : x.en} n={x._n} text={x[lang]} lang={lang} admin={admin} onChanged={refresh} />)}
              </ul>
            </div>
          ))}
        </div>
      )}
      {admin && (
        <div>
          {adding ? (
            <div className="flex flex-col gap-1.5 max-w-3xl">
              <label className="text-[11.5px] text-slate-500">{t.group}{' '}
                <select value={addGrp} onChange={e => setAddGrp(e.target.value)} className="ml-1 rounded border border-slate-300 px-1 py-0.5 text-[12px]">
                  {GROUP_ORDER.map(k => <option key={k} value={k}>{GROUP_LABEL[k][lang]}</option>)}
                </select>
              </label>
              <NoteEditor n={null} lang={lang} onDone={addFinding} />
            </div>
          ) : (
            <button type="button" onClick={() => setAdding(true)} className={`${btn} border-slate-200 text-slate-600 hover:bg-slate-50`}>+ {t.add}</button>
          )}
        </div>
      )}

      {/* timeline */}
      {years.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{GROUP_LABEL.timeline[lang]}</div>
          {years.map(({ y, rows }, yi) => {
            const open = filtering || (openYears[y] ?? yi < 2);
            return (
              <div key={y} className="flex flex-col">
                <button type="button" onClick={() => setOpenYears(o => ({ ...o, [y]: !open }))}
                  className="flex items-center gap-2 text-left py-1 text-[13px] font-bold text-slate-700 hover:text-brand-700">
                  <span className="text-slate-400 w-3">{open ? '▾' : '▸'}</span>{y}
                  <span className="text-[11px] font-normal text-slate-400">{rows.length} {lang === 'id' ? 'entri' : (rows.length > 1 ? 'entries' : 'entry')}</span>
                </button>
                {open && (
                  <div className="flex flex-col border-l-2 border-brand-200 ml-1.5 mb-1">
                    {rows.map(([when, x]) => (
                      <div key={when + x.en} className="flex gap-3 pl-3 py-0.5 text-[12.5px]">
                        <span className="font-mono text-brand-700 w-20 shrink-0">{when}</span>
                        {(() => { const k = TL_KIND[tlKind(x.en)]; return (
                          <span className="shrink-0 self-start mt-0.5 w-16 text-center rounded border text-[10.5px] font-bold" style={{ color: k.color, borderColor: k.color }}>{k[lang]}</span>
                        ); })()}
                        <ul className="list-none p-0 m-0 text-slate-600"><NoteLine n={x._n} text={x[lang]} lang={lang} admin={admin} onChanged={refresh} /></ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* editor over every note of this landscape */}
      {editor && (
        <div className="flex flex-col gap-3 border-t border-slate-200 pt-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[13px] font-bold text-slate-700">{t.editor}</span>
            <label className="text-[12px] text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={onlyReview} onChange={e => setOnlyReview(e.target.checked)} /> {t.onlyReview}
            </label>
          </div>
          {Object.keys(KIND_LABEL).map(kind => {
            const list = allNotes.filter(n => n.kind === kind && (!onlyReview || (n.active && n.needs_review === 1)));
            if (!list.length) return null;
            return (
              <div key={kind} className="flex flex-col gap-1">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{KIND_LABEL[kind][lang]} · {list.length}</div>
                <ul className="list-disc pl-4 text-[12.5px] text-slate-600 space-y-1">
                  {list.map(n => (
                    <NoteLine key={n.id} n={n} lang={lang} admin={admin} onChanged={refresh}
                      text={<>
                        {(n.flow || n.grp || n.when_label) && <span className="font-mono text-[11px] text-slate-400 mr-1">[{[n.flow, n.step, n.grp, n.when_label, n.status].filter(Boolean).join(' · ')}]</span>}
                        {lang === 'id' ? (n.text_id || n.text_en) : n.text_en}
                      </>} />
                  ))}
                </ul>
              </div>
            );
          })}
          {live && live.snapshots && live.snapshots.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.lastRuns}</div>
              <ul className="list-none p-0 m-0 text-[12px] text-slate-600 font-mono space-y-0.5">
                {live.snapshots.map(s => (
                  <li key={s.id}>#{s.id} · {s.started_at} · {s.period_en} · {s.status}{s.lands ? ' · ' + s.lands : ''} · {s.trigger_by}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
